import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MessageSquare, Send, Bot, User, AlertCircle, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import AIConsentDialog, { useAIConsent } from '../components/AIConsentDialog';

export const ChatPage = () => {
  const { API, token, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const messagesEndRef = useRef(null);
  
  // AI Consent state
  const { 
    hasConsent, 
    showConsentDialog, 
    setShowConsentDialog,
    acceptConsent, 
    declineConsent 
  } = useAIConsent();

  useEffect(() => {
    // Only fetch history if user has consented
    if (hasConsent === true) {
      fetchChatHistory();
    } else if (hasConsent === false) {
      setHistoryLoading(false);
    } else {
      // Show consent dialog if not yet decided
      setShowConsentDialog(true);
      setHistoryLoading(false);
    }
  }, [hasConsent]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await axios.get(`${API}/chat/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const history = response.data.reverse();
      const formattedHistory = [];
      history.forEach(msg => {
        formattedHistory.push({ role: 'user', content: msg.message });
        formattedHistory.push({ role: 'assistant', content: msg.response });
      });
      setMessages(formattedHistory);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    // Check for AI consent before sending
    if (hasConsent !== true) {
      setShowConsentDialog(true);
      return;
    }

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Failed to get response');
      console.error('Chat error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)]" data-testid="chat-page">
      {/* AI Consent Dialog */}
      <AIConsentDialog
        isOpen={showConsentDialog}
        onAccept={acceptConsent}
        onDecline={declineConsent}
        feature="AI Health Assistant"
      />

      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-4">
          <MessageSquare className="w-10 h-10 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl md:text-3xl font-nunito font-bold text-[#2D2D2D]">
              AI Health Assistant
            </h1>
            <p className="text-[#57534E]">
              Ask questions about PANDAS/PANS, treatments, and management strategies
            </p>
          </div>
        </div>
      </div>

      {/* Consent Declined State */}
      {hasConsent === false && (
        <Card className="flex-1 bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldOff className="w-8 h-8 text-stone-400" />
            </div>
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">
              AI Features Disabled
            </h2>
            <p className="text-[#57534E] mb-4">
              You've declined AI data sharing. To use the AI Health Assistant, you'll need to accept data sharing with OpenAI.
            </p>
            <Button
              onClick={() => setShowConsentDialog(true)}
              className="bg-primary hover:bg-primary/90"
            >
              Review Data Sharing Settings
            </Button>
          </div>
        </Card>
      )}

      {/* Chat Messages - Only show if consent given */}
      {hasConsent === true && (
        <>
          <Card className="flex-1 bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 overflow-y-auto mb-4">
        {historyLoading ? (
          <div className="text-center py-12">
            <p className="text-[#A8A29E]">Loading chat history...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <Bot className="w-16 h-16 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">
              Welcome to Your AI Health Assistant
            </h3>
            <p className="text-[#57534E] mb-6 max-w-md mx-auto">
              I'm here to help answer your questions about PANDAS/PANS, treatment options, symptom management, and more.
            </p>
            <div className="grid gap-3 max-w-lg mx-auto">
              {[
                'What are the main symptoms of PANDAS?',
                'How can I manage histamine intolerance?',
                'What supplements help with MCAS?',
                'Tell me about mycoplasma pneumonia'
              ].map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => setInput(suggestion)}
                  className="p-3 bg-primary/5 hover:bg-primary/10 rounded-lg text-left text-[#57534E] text-sm transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  </div>
                )}
                <div
                  className={`max-w-[80%] p-4 rounded-xl ${
                    message.role === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-stone-100 text-[#2D2D2D]'
                  }`}
                  data-testid={`message-${message.role}-${index}`}
                >
                  <p className="leading-relaxed whitespace-pre-wrap">{message.content}</p>
                </div>
                {message.role === 'user' && (
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-accent" strokeWidth={1.5} />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <div className="bg-stone-100 p-4 rounded-xl">
                  <div className="flex gap-2">
                    <div className="w-2 h-2 rounded-full bg-primary animate-bounce" />
                    <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </Card>

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="flex gap-3">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask me anything about PANDAS/PANS..."
          className="flex-1 bg-white border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-full h-14 px-6 text-base"
          disabled={loading}
          data-testid="chat-input"
        />
        <Button
          type="submit"
          disabled={loading || !input.trim()}
          className="bg-primary text-white hover:bg-primary/90 rounded-full h-14 px-6 font-semibold"
          data-testid="send-message-button"
        >
          <Send className="w-5 h-5" />
        </Button>
      </form>

      {/* Medical Disclaimer */}
      <div className="mt-4 flex items-start gap-2 text-xs text-[#A8A29E] bg-stone-50 rounded-lg p-3">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p className="italic">
          This AI assistant provides educational information only and is not a substitute for professional medical advice, diagnosis, or treatment. Always consult your child's healthcare provider with any questions regarding their health.
        </p>
      </div>
      </>
      )}
    </div>
  );
};

export default ChatPage;
